/**
 * Handwritten Preview Panel
 * ==========================
 * Displays OCR results from handwritten document processing.
 */
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { 
  FileText, Languages, Eye, ChevronDown, ChevronUp, 
  CheckCircle, AlertCircle, Loader2, Copy, Check
} from 'lucide-react';
import { copyToClipboard } from '../../utils/clipboard';
import { toast } from 'sonner';

const HandwrittenPreviewPanel = ({ visionAI, onReprocess }) => {
  const [showFullText, setShowFullText] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!visionAI) {
    return (
      <Card data-testid="handwritten-preview-panel">
        <CardContent className="py-8 text-center text-slate-500">
          <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p>No OCR data available</p>
          <p className="text-sm">Process the document to extract text</p>
        </CardContent>
      </Card>
    );
  }

  const ocrData = visionAI.handwriting_ocr || visionAI.standard_ocr || {};
  const layoutData = visionAI.layout_analysis?.analysis || {};
  
  const extractedText = ocrData.extracted_text || '';
  const confidence = ocrData.confidence || 0;
  const detectedLanguage = ocrData.detected_language || 'unknown';
  const handwritingQuality = ocrData.handwriting_quality || 'unknown';
  const sections = ocrData.sections || [];
  const detectedEntities = ocrData.detected_entities || {};

  const copyText = () => {
    copyToClipboard(extractedText).then(ok => {
      if (ok) {
        setCopied(true);
        toast.success('Text copied to clipboard');
        setTimeout(() => setCopied(false), 2000);
      } else {
        toast.error('Copy failed');
      }
    });
  };

  const getConfidenceColor = (conf) => {
    if (conf >= 0.8) return 'bg-green-100 text-green-700';
    if (conf >= 0.5) return 'bg-yellow-100 text-yellow-700';
    return 'bg-red-100 text-red-700';
  };

  const getQualityBadge = (quality) => {
    const colors = {
      clear: 'bg-green-100 text-green-700',
      moderate: 'bg-yellow-100 text-yellow-700',
      poor: 'bg-red-100 text-red-700'
    };
    return colors[quality] || 'bg-slate-100 text-slate-700';
  };

  // Language display names
  const languageNames = {
    en: 'English', hi: 'Hindi', bn: 'Bengali', ta: 'Tamil',
    te: 'Telugu', mr: 'Marathi', gu: 'Gujarati', kn: 'Kannada',
    ml: 'Malayalam', pa: 'Punjabi', or: 'Odia', as: 'Assamese',
    ur: 'Urdu', sa: 'Sanskrit'
  };

  return (
    <Card data-testid="handwritten-preview-panel">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-purple-600" />
              OCR Results
            </CardTitle>
            <CardDescription>
              Extracted text from document image
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={getConfidenceColor(confidence)}>
              {Math.round(confidence * 100)}% confidence
            </Badge>
            {layoutData.quality?.is_handwritten && (
              <Badge variant="outline" className="bg-purple-50 text-purple-700">
                Handwritten
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Metadata Row */}
        <div className="flex flex-wrap gap-3 p-3 bg-slate-50 rounded-lg">
          <div className="flex items-center gap-2">
            <Languages className="w-4 h-4 text-slate-500" />
            <span className="text-sm font-medium">
              {languageNames[detectedLanguage] || detectedLanguage}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Eye className="w-4 h-4 text-slate-500" />
            <span className="text-sm">
              Quality: <Badge className={getQualityBadge(handwritingQuality)} variant="secondary">
                {handwritingQuality}
              </Badge>
            </span>
          </div>
          {ocrData.script_type && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-600">
                Script: {ocrData.script_type}
              </span>
            </div>
          )}
        </div>

        {/* Extracted Text */}
        <div className="relative">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-700">Extracted Text</span>
            <Button variant="ghost" size="sm" onClick={copyText}>
              {copied ? (
                <Check className="w-4 h-4 text-green-600" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
            </Button>
          </div>
          <div 
            className={`p-4 bg-white border rounded-lg font-mono text-sm whitespace-pre-wrap ${
              !showFullText && extractedText.length > 500 ? 'max-h-40 overflow-hidden' : ''
            }`}
          >
            {extractedText || 'No text extracted'}
          </div>
          {extractedText.length > 500 && (
            <Button
              variant="ghost"
              size="sm"
              className="w-full mt-2"
              onClick={() => setShowFullText(!showFullText)}
            >
              {showFullText ? (
                <>
                  <ChevronUp className="w-4 h-4 mr-1" />
                  Show Less
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4 mr-1" />
                  Show Full Text ({extractedText.length} chars)
                </>
              )}
            </Button>
          )}
        </div>

        {/* Detected Sections */}
        {sections.length > 0 && (
          <div>
            <span className="text-sm font-medium text-slate-700 mb-2 block">
              Detected Sections ({sections.length})
            </span>
            <div className="space-y-2">
              {sections.slice(0, 5).map((section, idx) => (
                <div key={idx} className="p-2 bg-slate-50 rounded border-l-2 border-purple-400">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="outline" className="text-xs capitalize">
                      {section.type}
                    </Badge>
                    {section.confidence && (
                      <span className="text-xs text-slate-500">
                        {Math.round(section.confidence * 100)}%
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-600 line-clamp-2">
                    {section.text}
                  </p>
                </div>
              ))}
              {sections.length > 5 && (
                <p className="text-sm text-slate-500 text-center">
                  +{sections.length - 5} more sections
                </p>
              )}
            </div>
          </div>
        )}

        {/* Detected Entities */}
        {Object.keys(detectedEntities).length > 0 && (
          <div>
            <span className="text-sm font-medium text-slate-700 mb-2 block">
              Quick Entities Found
            </span>
            <div className="flex flex-wrap gap-2">
              {detectedEntities.dates?.map((d, i) => (
                <Badge key={`date-${i}`} variant="outline" className="bg-blue-50">
                  📅 {d}
                </Badge>
              ))}
              {detectedEntities.amounts?.map((a, i) => (
                <Badge key={`amt-${i}`} variant="outline" className="bg-green-50">
                  💰 {a}
                </Badge>
              ))}
              {detectedEntities.names?.slice(0, 3).map((n, i) => (
                <Badge key={`name-${i}`} variant="outline" className="bg-purple-50">
                  👤 {n}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Reprocess Button */}
        {onReprocess && (
          <Button
            variant="outline"
            className="w-full"
            onClick={onReprocess}
          >
            <Loader2 className="w-4 h-4 mr-2" />
            Reprocess with AI
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default HandwrittenPreviewPanel;
